import type { MarginPreset, PaperSize, PdfOptions } from "./types";

type PaperSizeConfig = {
  cssName: "A4" | "Letter";
  width: number;
  height: number;
  unit: "mm" | "in";
};

type MarginConfig = {
  page: string;
  preview: string;
};

export const paperSizes = {
  a4: {
    cssName: "A4",
    width: 210,
    height: 297,
    unit: "mm",
  },
  letter: {
    cssName: "Letter",
    width: 8.5,
    height: 11,
    unit: "in",
  },
} satisfies Record<PaperSize, PaperSizeConfig>;

export const marginPresets = {
  narrow: {
    page: "12mm",
    preview: "12mm",
  },
  normal: {
    page: "18mm",
    preview: "18mm",
  },
  wide: {
    page: "25mm",
    preview: "25mm",
  },
} satisfies Record<MarginPreset, MarginConfig>;

export function getPageSizeValue(options: PdfOptions) {
  return `${paperSizes[options.paperSize].cssName} ${options.orientation}`;
}

export function getPageMarginValue(options: PdfOptions) {
  return marginPresets[options.margins].page;
}

export function getPreviewMarginValue(options: PdfOptions) {
  return marginPresets[options.margins].preview;
}

export function getPdfPageDimensions(options: PdfOptions) {
  const paperSize = paperSizes[options.paperSize];

  return {
    width:
      options.orientation === "portrait" ? paperSize.width : paperSize.height,
    height:
      options.orientation === "portrait" ? paperSize.height : paperSize.width,
  };
}

export function getPdfPageAspectRatio(options: PdfOptions) {
  const { width, height } = getPdfPageDimensions(options);

  return `${width} / ${height}`;
}

export function getPdfPagePreviewWidth(options: PdfOptions) {
  const paperSize = paperSizes[options.paperSize];
  const { width } = getPdfPageDimensions(options);

  return `${width}${paperSize.unit}`;
}

export function getPdfPagePreviewHeight(options: PdfOptions) {
  const paperSize = paperSizes[options.paperSize];
  const { height } = getPdfPageDimensions(options);

  return `${height}${paperSize.unit}`;
}

export function getPdfPagePreviewMargin(options: PdfOptions) {
  return getPreviewMarginValue(options);
}

export function parseCssLengthToMm(value: string) {
  const match = /^(\d+(?:\.\d+)?)(mm|in)$/.exec(value.trim());

  if (!match) {
    throw new TypeError(`Unsupported CSS length: ${value}`);
  }

  const amount = Number(match[1]);

  return match[2] === "in" ? amount * 25.4 : amount;
}

export function getPdfPageDimensionsMm(options: PdfOptions) {
  const paperSize = paperSizes[options.paperSize];
  const { width, height } = getPdfPageDimensions(options);

  if (paperSize.unit === "mm") {
    return { widthMm: width, heightMm: height };
  }

  return {
    widthMm: width * 25.4,
    heightMm: height * 25.4,
  };
}

/** CSS pixel size of the paper box at 96dpi (matches browser iframe layout). */
export function getPdfPageViewportSize(options: PdfOptions) {
  const { widthMm, heightMm } = getPdfPageDimensionsMm(options);
  const mmToCssPx = 96 / 25.4;

  return {
    width: Math.round(widthMm * mmToCssPx),
    height: Math.round(heightMm * mmToCssPx),
  };
}

/**
 * Margin insets as percentages of the rendered page box so they scale when the
 * preview sheet is narrower than full paper width (split pane). Absolute mm
 * insets would eat a disproportionate share of a scaled page and over-paginate.
 */
export function getPdfPagePreviewMarginInsetStyle(options: PdfOptions) {
  const marginMm = parseCssLengthToMm(getPreviewMarginValue(options));
  const { widthMm, heightMm } = getPdfPageDimensionsMm(options);
  const x = `${(marginMm / widthMm) * 100}%`;
  const y = `${(marginMm / heightMm) * 100}%`;

  return {
    top: y,
    right: x,
    bottom: y,
    left: x,
  };
}

export function getPdfPagePreviewContainStyle(options: PdfOptions) {
  const { width, height } = getPdfPageDimensions(options);

  return {
    aspectRatio: `${width} / ${height}`,
    width: `min(100cqw, calc(100cqh * ${width} / ${height}))`,
  };
}
